/**
 * Roll-off hires, as `/api/rolloff-opportunities` sends them.
 *
 * Field for field with `BusinessLogic/Models/RollOffOpportunityDtos.cs`. The
 * stage arrives as three things — id to move by, code to compare by, name to
 * print — plus the outcome, so a client can grey out a finished hire without
 * having to recognise the word "Fulfillment". Nothing in this feature compares
 * a stage by its name; see `shared/stage-catalog.ts` for why.
 */

/** A hire, as a list row or a card. */
export interface RollOffOpportunity {
  id: number;
  leadStageId: number;
  /** Which stage this is. Branch, group and colour on this. */
  leadStageCode: string;
  /** What to print. The tenant's wording, and theirs to change. */
  leadStageName: string;
  /** `open` | `won` | `lost`. */
  leadStageOutcome: string;
  stageEnteredAt: string;
  /** Null until winning the hire makes this site a customer. */
  customerId: number | null;
  siteName: string;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  /** Which container size, by id — what the form sends back. */
  containerTypeId: number | null;
  /** What the size IS. Group and filter on this, never on the label. */
  containerTypeCode: string | null;
  /** What to print. The tenant's wording, and theirs to change. */
  containerTypeLabel: string | null;
  /**
   * What a rep typed before the catalog existed, on the hires whose text the
   * 2026-09-03 backfill would not guess at — "large", "roro", "3.5t". Nothing
   * writes this; the card prints it when there is no type, so a size somebody
   * wrote down is never simply gone from the screen.
   */
  containerSizeText: string | null;
  quotedPrice: number | null;
  /** `YYYY-MM-DD` — a `DateOnly` on the wire, not an instant. */
  hireStartsOn: string | null;
  hireEndsOn: string | null;
  salesRepUserId: number | null;
  outcomeAt: string | null;
  outcomeReason: string | null;
  createdAt: string;
  updatedAt: string | null;
}

/**
 * One step of the trail. Both ends carry a code and a name for the same
 * reason a hire does: a reader compares one and prints the other.
 */
export interface RollOffStageHistoryEntry {
  fromLeadStageId: number;
  fromLeadStageCode: string;
  fromLeadStageName: string;
  toLeadStageId: number;
  toLeadStageCode: string;
  toLeadStageName: string;
  note: string | null;
  changedByActorType: string;
  changedByActorId: number | null;
  changedAt: string;
}

/** A hire and how it got where it is. The server orders the trail newest first. */
export interface RollOffOpportunityDetail {
  opportunity: RollOffOpportunity;
  history: RollOffStageHistoryEntry[];
}

export interface RollOffOpportunityPage {
  total: number;
  page: number;
  pageSize: number;
  items: RollOffOpportunity[];
}

/**
 * What a caller may set on a hire, on create and on update alike — one shape
 * for both, because the server's write is not a patch and a null here clears
 * the column. `SaveRollOffOpportunityRequest` is a single record for exactly
 * this reason; two shapes here would be the same list written twice.
 *
 * The stage is deliberately absent. A hire starts at the front of the
 * vocabulary and moves only by a transition that records who moved it and why.
 */
export interface SaveRollOffOpportunity {
  customerId: number | null;
  siteName: string;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  /**
   * The size, by catalog id. Null is a hire nobody has sized yet, which is
   * normal and is not an error.
   *
   * There is no free-text counterpart, and that is the whole change: a caller
   * cannot invent a size any more. `containerSizeText` is read-only, so
   * omitting it here cannot erase what an old hire had written on it.
   */
  containerTypeId: number | null;
  quotedPrice: number | null;
  hireStartsOn: string | null;
  hireEndsOn: string | null;
  salesRepUserId: number | null;
}

/**
 * One container size the tenant offers.
 *
 * `code` and `label` are two fields for the same reason a stage has both: the
 * code is what a report groups by, the label is what a rep reads and what the
 * tenant may rewrite. Nothing in this app compares a container by its label —
 * that habit is what made "30 yd", "30yd" and "30 Yard" three different sizes.
 */
export interface RollOffContainerType {
  id: number;
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

/**
 * One board column's tally, over the WHOLE filter rather than over the page of
 * cards the board is holding. `column` matches a hire's `leadStageCode`.
 */
export interface RollOffStageFacet {
  column: string;
  label: string;
  count: number;
  quotedTotal: number;
}

export interface RollOffStageFacets {
  totalCount: number;
  totalQuoted: number;
  columns: RollOffStageFacet[];
}

/** The one place the hire endpoints are spelled. */
/**
 * Where the skip goes, written for a person.
 *
 * Beside the model rather than on a page, because both the Table and the Board
 * print it and the second one asked for it by copying the first. A hire with
 * no address at all reads as a dash - the same absence the table's other cells
 * show, rather than an empty line that looks like a rendering fault.
 */
export function hireAddress(hire: RollOffOpportunity): string {
  const parts = [hire.addressLine1, hire.city, hire.postalCode].filter(part => !!part);
  return parts.length ? parts.join(', ') : '—';
}

export const ROLLOFF_OPPORTUNITIES_URL = '/api/rolloff-opportunities';

/**
 * The per-column counts, over the whole filter. Fetched separately from the
 * page of cards, because they answer a different question: the page says what
 * is on screen and this says what there is.
 */
export const ROLLOFF_FACETS_URL = `${ROLLOFF_OPPORTUNITIES_URL}/facets`;

/** The tenant's container sizes, for the form that files a hire against one. */
export const ROLLOFF_CONTAINER_TYPES_URL =
  `${ROLLOFF_OPPORTUNITIES_URL}/container-types`;

/**
 * Every deal, written out as the server writes it.
 *
 * The transfer route rather than this controller's: import, export and the
 * column schema are one endpoint family, keyed by the table's transfer name.
 * Named here because two views hand it to the same shared button.
 */
export const ROLLOFF_TRANSFER_EXPORT_URL =
  '/api/rolloff-hires/transfer/export';

/**
 * The largest page the server will return, whatever is asked for —
 * `RollOffReadLimits.MaxPageSize`. Stated here so the board asks for what it
 * can actually be given: it used to ask for 200 and be handed 100 without
 * either side saying so.
 */
export const ROLLOFF_MAX_PAGE_SIZE = 100;

/**
 * The two reads a board's columns come from.
 *
 * There is no single "catalog" endpoint. This board briefly called one that
 * had been removed the day before, and every check passed: the specs mock
 * HTTP, and an AOT build does not know a URL from any other string. The route
 * contract test is what found it - after the board had shipped 404ing on load,
 * because the first request in ngOnInit is this one and nothing else runs
 * until it succeeds.
 */
export const LEAD_STAGES_URL = '/api/lead-stages';
export const LEAD_STAGE_TRANSITIONS_URL = '/api/lead-stages/transitions';

/**
 * Which vocabulary this board asks for. Not optional: the endpoint reads an
 * ABSENT `vocabulary` as the lead one — that is what every caller written
 * before a second vocabulary existed was asking for — so a board that forgets
 * it draws the wrong six columns and every move it offers is refused.
 */
export const ROLLOFF_VOCABULARY = 'rolloff';
